/**
 * 메일 설정
 * ---------------------------------------------------------------------------
 * 왜 이 화면이 있는가
 *   메일 설정이 `.env` 에만 있었다. 그래서 메일 서버를 바꾸거나 앱 비밀번호를
 *   다시 발급받을 때마다 파일을 찾아 고치고 **서버를 재기동**해야 했다.
 *   실제로 고쳐 두고 재기동을 잊어, 고객에게 가야 할 가입 인증코드가 콘솔에만
 *   찍힌 일이 있었다.
 *
 *   여기서 저장하면 **다음 발송부터 바로** 쓰인다. 발송기가 설정이 바뀐 것을
 *   보고 연결을 다시 연다.
 *
 * 확인을 두 단계로 나눈 이유
 *   「연결 확인」은 주소·포트·계정이 맞는지만 본다. 메일은 보내지 않는다.
 *   「시험 발송」은 실제로 한 통 보낸다. **연결이 되어도 수신함에 안 들어올 수
 *   있어서**(스팸 분류 등) 둘을 나눠 두었다. 받은 것을 눈으로 확인하기 전에는
 *   된다고 말할 수 없는 기능이다.
 *
 * 비밀번호는 되받지 않는다
 *   서버가 값을 내보내지 않는다. 저장되어 있는지 여부만 알려 준다. 그래서
 *   비워 두고 저장하면 기존 비밀번호가 유지된다 — 포트 하나 고치려다 비밀번호가
 *   날아가면 안 된다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { MailOutlined, SendOutlined, ApiOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface MailSettings {
  provider: 'none' | 'smtp' | 'sens';
  sensServiceId: string;
  sensAccessKey: string;
  hasSensSecretKey: boolean;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  hasPassword: boolean;
  fromAddress: string;
  fromName: string;
  signupNotifyAddress: string;
  source: 'db' | 'env';
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
  updatedAt: string | null;
}

interface CheckResult {
  ok: boolean;
  message: string;
}

interface RecentMail {
  messageLogId: string;
  templateCode: string;
  subject: string | null;
  status: string;
  provider: string | null;
  providerMsgId: string | null;
  queuedAt: string;
  errorMessage: string | null;
}

/**
 * 지금 메일이 나갈 수 있는 상태인가.
 *
 * 화면에 칸이 열 개 넘게 있어서, 저장해 놓고도 **무엇이 비어 있는지** 몰라
 * 한참을 들여다보게 된다. 맨 위에 한 줄로 답한다 — 되는가, 안 되면 무엇이
 * 모자란가.
 *
 * 「설정이 다 찼다」와 「실제로 보내진다」는 다르다. 그래서 마지막 확인
 * 결과를 함께 말한다. 도메인 인증(SPF·DKIM)이 없으면 설정이 멀쩡해도
 * 스팸으로 떨어진다.
 */
function mailReadiness(d: MailSettings | undefined): {
  level: 'ok' | 'warning' | 'info';
  text: string;
  detail: string;
} | null {
  if (!d) return null;

  if (d.provider === 'none') {
    return {
      level: 'info',
      text: '메일을 보내지 않도록 설정되어 있습니다',
      detail: '가입 인증코드·초대 메일이 나가지 않습니다. 발송 경로를 골라 주세요.',
    };
  }

  const missing: string[] = [];
  if (d.provider === 'sens') {
    if (!d.sensServiceId.trim()) missing.push('서비스 ID');
    if (!d.sensAccessKey.trim()) missing.push('접근키 ID');
    if (!d.hasSensSecretKey) missing.push('비밀키');
  } else {
    if (!d.host.trim()) missing.push('서버 주소');
    if (!d.user.trim()) missing.push('로그인 계정');
    if (!d.hasPassword) missing.push('비밀번호');
  }
  if (!d.fromAddress.trim()) missing.push('발신 주소');

  const route = d.provider === 'sens' ? '네이버 클라우드 SENS' : `SMTP (${d.host})`;

  if (missing.length > 0) {
    return {
      level: 'warning',
      text: `${route} 로 보내도록 되어 있지만 설정이 덜 찼습니다`,
      detail: `${missing.join(', ')} 을(를) 채워 주세요. 지금은 메일이 나가지 않습니다.`,
    };
  }

  // 설정은 다 찼다. 실제로 나갔는지는 마지막 확인 결과가 말해 준다.
  if (d.lastTestOk === true) {
    return {
      level: 'ok',
      text: `${route} 로 연결되어 있습니다`,
      detail: `마지막 확인 ${d.lastTestedAt ? new Date(d.lastTestedAt).toLocaleString('ko-KR') : ''} 성공. 발신 주소는 ${d.fromAddress} 입니다.`,
    };
  }

  if (d.lastTestOk === false) {
    return {
      level: 'warning',
      text: `${route} 설정은 채워져 있지만 마지막 확인이 실패했습니다`,
      detail: d.lastTestError ?? '아래 「시험 발송」으로 다시 확인해 주세요.',
    };
  }

  return {
    level: 'info',
    text: `${route} 로 보내도록 설정되어 있습니다`,
    detail: '아직 확인해 본 적이 없습니다. 아래 「시험 발송」으로 한 통 받아 보세요.',
  };
}

/** 자주 쓰는 메일 서버. 고를 때마다 포트·보안 연결까지 함께 맞춘다 */
const PRESETS: Record<string, { host: string; port: number; secure: boolean; hint: string }> = {
  Gmail: {
    host: 'smtp.gmail.com',
    port: 465,
    secure: true,
    hint: '비밀번호는 구글 계정 비밀번호가 아니라 **앱 비밀번호**입니다. 2단계 인증을 켜야 발급됩니다. 하루 약 500통 제한이 있고, 발신 주소가 로그인 계정으로 고정됩니다.',
  },
  'NCP Cloud Outbound Mailer': {
    host: 'email-smtp.ncloud.com',
    port: 465,
    secure: true,
    hint: 'NCP 콘솔에서 발급한 SMTP 계정을 씁니다. 발신 주소는 사전에 인증된 도메인이어야 합니다.',
  },
  '직접 입력': { host: '', port: 587, secure: false, hint: '' },
};

export function MailSettingPage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [form] = Form.useForm();
  const [preset, setPreset] = useState<string>('직접 입력');
  const [testTo, setTestTo] = useState('');
  const [check, setCheck] = useState<CheckResult | null>(null);

  const settings = useQuery<MailSettings, NormalizedError>({
    queryKey: ['admin', 'messaging', 'mail'],
    queryFn: async () => (await apiClient.get<MailSettings>('/admin/messaging/mail')).data,
  });

  useEffect(() => {
    if (!settings.data) return;
    form.setFieldsValue({
      provider: settings.data.provider,
      sensServiceId: settings.data.sensServiceId,
      sensAccessKey: settings.data.sensAccessKey,
      sensSecretKey: '',
      host: settings.data.host,
      port: settings.data.port,
      secure: settings.data.secure,
      user: settings.data.user,
      password: '',
      fromAddress: settings.data.fromAddress,
      fromName: settings.data.fromName,
      signupNotifyAddress: settings.data.signupNotifyAddress,
    });
    /*
     * 받을 주소를 발신 주소로 미리 채우지 않는다.
     *
     * 예전에는 그렇게 했는데, 네이버 클라우드는 **보내는 주소와 받는 주소가
     * 같으면 거부**한다. 그래서 아무것도 고치지 않고 누르기만 하면 반드시
     * 실패했다 — "메일이 안 나간다" 로 보이지만 설정은 멀쩡한 경우다.
     * SMTP 때는 자기에게 보내기가 통해서 드러나지 않았다.
     */
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: async (values: Record<string, unknown>) =>
      (await apiClient.post<MailSettings>('/admin/messaging/mail', values)).data,
    onSuccess: async () => {
      setCheck(null);
      message.success('저장했습니다. 다음 발송부터 이 설정이 쓰입니다.');
      await client.invalidateQueries({ queryKey: ['admin', 'messaging'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  const verify = useMutation({
    mutationFn: async () =>
      (await apiClient.post<CheckResult>('/admin/messaging/mail/verify')).data,
    onSuccess: async (result) => {
      setCheck(result);
      await client.invalidateQueries({ queryKey: ['admin', 'messaging', 'mail'] });
    },
    onError: (err: NormalizedError) => setCheck({ ok: false, message: errorText(err) }),
  });

  /*
   * 최근 발송 이력.
   *
   * 「발송됨」은 네이버 클라우드가 **접수했다**는 뜻일 뿐이다. 그 뒤에
   * 실패하면 우리 이력에는 성공으로 남는다 — 그 차이를 여기서 확인한다.
   */
  const recent = useQuery<RecentMail[], NormalizedError>({
    queryKey: ['admin', 'messaging', 'mail', 'recent'],
    queryFn: async () => (await apiClient.get<RecentMail[]>('/admin/messaging/mail/recent')).data,
    retry: false,
  });

  const [statusOf, setStatusOf] = useState<Record<string, string>>({});

  const askStatus = async (row: RecentMail) => {
    if (!row.providerMsgId) return;
    setStatusOf((p) => ({ ...p, [row.messageLogId]: '확인 중…' }));
    try {
      const { data } = await apiClient.get<{ ok: boolean; httpStatus: number; body: string }>(
        `/admin/messaging/mail/requests/${encodeURIComponent(row.providerMsgId)}`,
      );
      setStatusOf((p) => ({
        ...p,
        [row.messageLogId]: `HTTP ${data.httpStatus} · ${data.body.slice(0, 500)}`,
      }));
    } catch (err) {
      setStatusOf((p) => ({ ...p, [row.messageLogId]: errorText(err as NormalizedError) }));
    }
  };

  const sendTest = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<{ delivered: boolean; error?: string }>('/admin/messaging/test', {
          channel: 'EMAIL',
          recipient: testTo,
        })
      ).data,
    onSuccess: async (result) => {
      if (result.delivered) {
        message.success(`${testTo} 로 보냈습니다. 받은편지함과 스팸함을 확인해 주세요.`);
      } else {
        message.warning(result.error ?? '보내지 못했습니다. 설정을 확인해 주세요.');
      }
      // 시험 발송 결과가 「마지막 확인」에 적힌다. 상태 표시를 다시 읽어 온다.
      await client.invalidateQueries({ queryKey: ['admin', 'messaging', 'mail'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  const submit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    save.mutate(values);
  };

  const applyPreset = (name: string) => {
    setPreset(name);
    const p = PRESETS[name];
    if (!p || !p.host) return;
    form.setFieldsValue({ host: p.host, port: p.port, secure: p.secure });
  };

  const data = settings.data;
  const usingEnv = data?.source === 'env';
  const readiness = mailReadiness(data);
  const isSens = data?.provider === 'sens';

  /** 자기 자신에게 보내려는 중인가. 네이버 클라우드가 거부한다. */
  const selfSend =
    isSens &&
    Boolean(testTo.trim()) &&
    testTo.trim().toLowerCase() === (data?.fromAddress ?? '').trim().toLowerCase();

  /** 발신 주소의 도메인. SENS 로 보내려면 이 도메인에 네이버 클라우드 SPF 가 있어야 한다. */
  const senderDomain = (data?.fromAddress ?? '').split('@')[1] ?? '';

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        loading={settings.isLoading}
        title={
          <Space>
            <MailOutlined />
            <span>메일 설정</span>
            {readiness && (
              <Tag
                color={
                  readiness.level === 'ok'
                    ? 'green'
                    : readiness.level === 'warning'
                      ? 'orange'
                      : 'default'
                }
              >
                {readiness.level === 'ok'
                  ? '연결됨'
                  : readiness.level === 'warning'
                    ? '확인 필요'
                    : '보내지 않음'}
              </Tag>
            )}
            {data && data.provider !== 'none' && (
              <Tag color="blue">
                {data.provider === 'sens' ? '네이버 클라우드 SENS' : 'SMTP'}
              </Tag>
            )}
          </Space>
        }
      >
        {/* 맨 위에서 한 줄로 답한다 — 지금 메일이 나가는가, 안 되면 무엇이 모자란가. */}
        {readiness && (
          <Alert
            type={readiness.level === 'ok' ? 'success' : readiness.level}
            showIcon
            style={{ marginBottom: 16 }}
            message={readiness.text}
            description={readiness.detail}
          />
        )}

        <Paragraph type="secondary">
          가입 인증코드, 구성원 초대, 무료 기간 만료 공지가 이 설정으로 나갑니다. 저장하면{' '}
          <strong>다음 발송부터 바로</strong> 적용되며 서버를 다시 띄울 필요가 없습니다.
        </Paragraph>

        {usingEnv && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="지금은 서버 환경설정(.env)의 값을 쓰고 있습니다"
            description="아래에서 저장하면 이 화면의 값이 우선합니다. 그때부터는 .env 를 고쳐도 반영되지 않습니다."
          />
        )}

        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item label="메일 서버 종류" extra="고르면 주소·포트·보안 연결이 함께 맞춰집니다">
            <Select
              value={preset}
              onChange={applyPreset}
              options={Object.keys(PRESETS).map((k) => ({ value: k, label: k }))}
              style={{ maxWidth: 320 }}
            />
          </Form.Item>

          {PRESETS[preset]?.hint && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={PRESETS[preset].hint.replace(/\*\*/g, '')}
            />
          )}

          <Form.Item name="provider" label="발송 경로" rules={[{ required: true }]}>
            <Select
              style={{ maxWidth: 420 }}
              options={[
                { value: 'sens', label: '네이버 클라우드 SENS (권장)' },
                { value: 'smtp', label: 'SMTP 서버' },
                { value: 'none', label: '보내지 않는다 (개발·점검용)' },
              ]}
            />
          </Form.Item>

          {/* ---- 네이버 클라우드 SENS ---- */}
          <Form.Item noStyle shouldUpdate={(a, b) => a.provider !== b.provider}>
            {({ getFieldValue }) =>
              getFieldValue('provider') !== 'sens' ? null : (
                <>
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 16, maxWidth: 820 }}
                    message="API 로 보내므로 보낸편지함에 쌓이지 않습니다"
                    description={
                      <>
                        사서함이 없는 발송 전용 서비스라, SMTP 처럼 보낸 메일이 계정에
                        쌓이지 않습니다. 월 한도도 넉넉합니다.
                        <br />
                        <b>다만 발신 주소의 도메인 인증(SPF·DKIM)은 따로 필요합니다.</b>{' '}
                        네이버 클라우드 콘솔에서 발신 주소를 등록하고 DNS 레코드를 추가한
                        뒤에 회사 도메인 주소로 바꾸세요. 인증 전에 바꾸면 스팸으로 분류됩니다.
                      </>
                    }
                  />

                  <Form.Item
                    name="sensServiceId"
                    label="서비스 ID"
                    extra="SENS 콘솔 > Project 의 「서비스 ID」를 그대로 붙여 넣으세요"
                  >
                    <Input
                      placeholder="ncp:mail:kr:260529391009:worknhub_mail"
                      style={{ width: 460 }}
                    />
                  </Form.Item>

                  <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
                    <Form.Item
                      name="sensAccessKey"
                      label="접근키 ID"
                      extra="마이페이지 > 계정 관리 > 인증키 관리"
                    >
                      <Input placeholder="Access Key ID" style={{ width: 300 }} />
                    </Form.Item>
                    <Form.Item
                      name="sensSecretKey"
                      label="비밀키"
                      extra={
                        data?.hasSensSecretKey
                          ? '저장되어 있습니다. 바꿀 때만 입력하세요'
                          : '아직 저장된 비밀키가 없습니다'
                      }
                    >
                      <Input.Password
                        placeholder={
                          data?.hasSensSecretKey ? '●●●●●●●● (그대로 둠)' : 'Secret Key'
                        }
                        style={{ width: 300 }}
                        autoComplete="new-password"
                      />
                    </Form.Item>
                  </Space>
                </>
              )
            }
          </Form.Item>

          {/* ---- SMTP ---- */}
          <Form.Item noStyle shouldUpdate={(a, b) => a.provider !== b.provider}>
            {({ getFieldValue }) =>
              getFieldValue('provider') === 'sens' ? null : (
                <>
                  <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
                    <Form.Item name="host" label="SMTP 서버 주소">
                      <Input placeholder="smtp.gmail.com" style={{ width: 260 }} />
                    </Form.Item>
                    <Form.Item name="port" label="포트" extra="465=보안 연결 · 587=STARTTLS">
                      <InputNumber min={1} max={65535} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name="secure" label="보안 연결(TLS)" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Space>

                  <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
                    <Form.Item name="user" label="로그인 계정">
                      <Input placeholder="worknit16@gmail.com" style={{ width: 280 }} />
                    </Form.Item>
                    <Form.Item
                      name="password"
                      label="비밀번호"
                      extra={
                        data?.hasPassword
                          ? '저장되어 있습니다. 바꿀 때만 입력하세요'
                          : '아직 저장된 비밀번호가 없습니다'
                      }
                    >
                      <Input.Password
                        placeholder={data?.hasPassword ? '●●●●●●●● (그대로 둠)' : '앱 비밀번호'}
                        style={{ width: 280 }}
                        autoComplete="new-password"
                      />
                    </Form.Item>
                  </Space>
                </>
              )
            }
          </Form.Item>

          <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
            <Form.Item
              name="fromAddress"
              label="발신 이메일 주소"
              extra="Gmail 은 로그인 계정과 다르면 제 주소로 바꿔 버립니다"
            >
              <Input placeholder="no-reply@worknit.co.kr" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="fromName" label="발신인 이름">
              <Input placeholder="(주)워크앤아이티" style={{ width: 240 }} />
            </Form.Item>
          </Space>

          <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
            <Form.Item
              name="signupNotifyAddress"
              label="가입 알림 받을 주소"
              extra="홍보 사이트에서 새로 가입하면 이 주소로 알립니다. 비우면 발신 주소로 갑니다"
            >
              <Input placeholder="sales@worknit.co.kr" style={{ width: 280 }} />
            </Form.Item>
          </Space>

          <Button type="primary" loading={save.isPending} onClick={() => void submit()}>
            저장
          </Button>
        </Form>
      </Card>

      <Card
        title={
          <Space>
            <ApiOutlined />
            <span>확인</span>
          </Space>
        }
      >
        <Paragraph type="secondary">
          {isSens ? (
            <>
              <strong>연결 확인</strong>은 메일을 보내지 않고 <b>서비스 ID·접근키·비밀키가
              맞는지만</b> 봅니다(네이버 클라우드에 조회를 한 번 겁니다).
              <br />
              <strong>시험 발송</strong>은 실제로 한 통 보냅니다. 키가 맞아도 도메인 인증이
              없으면 스팸으로 떨어지므로, <strong>받은 것을 눈으로 확인</strong>해 주세요
              (스팸함도 함께).
            </>
          ) : (
            <>
              <strong>연결 확인</strong>은 서버에 붙어 로그인까지만 해 봅니다(메일을 보내지
              않습니다).
              <br />
              <strong>시험 발송</strong>은 실제로 한 통 보냅니다. 연결이 되어도 수신함에 안
              들어올 수 있으니(스팸 분류 등) <strong>받은 것을 눈으로 확인</strong>해 주세요.
            </>
          )}
        </Paragraph>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/*
            접수와 도착은 다르다.

            네이버 클라우드는 요청을 받으면 202 로 "접수했다" 고만 답한다.
            발신 도메인이 인증되어 있지 않으면 그 뒤에 조용히 버려진다 —
            우리 화면에는 「발송됨」으로 남고 받는 사람은 받지 못한다.
            2026-10-23 에 실제로 그 상태였다(발신 worknit.co.kr 의 SPF 는
            네이버웍스를 가리키고 있었다).
          */}
          {isSens && senderDomain && (
            <Alert
              type="warning"
              showIcon
              message={`발신 도메인(${senderDomain})이 네이버 클라우드로 인증되어 있어야 합니다`}
              description={
                <>
                  보내는 쪽이 「발송됨」이라고 해도, 그것은 <b>네이버 클라우드가 접수했다</b>는
                  뜻이지 받는 사람에게 도착했다는 뜻이 아닙니다. 아래 두 가지가 없으면 받는
                  메일 서버가 위조로 보고 버립니다.
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    <li>
                      SENS 콘솔에 <b>{data?.fromAddress}</b> 를 발신 주소로 등록
                    </li>
                    <li>
                      <b>{senderDomain}</b> DNS 의 SPF 에{' '}
                      <code>include:email.ncloud.com</code> 포함 (DKIM 도 함께 권장)
                    </li>
                  </ul>
                  다른 메일 서비스(네이버웍스·구글 등)를 쓰던 도메인이면 그쪽 SPF 만 들어
                  있을 수 있습니다. 그때는 <b>네이버 클라우드용으로 설정해 둔 도메인</b>의
                  주소로 바꾸는 편이 빠릅니다.
                </>
              }
            />
          )}

          <Space wrap>
            <Tooltip
              title={
                isSens
                  ? '메일을 보내지 않고 키가 맞는지만 확인합니다.'
                  : '메일을 보내지 않고 서버 접속·로그인만 해 봅니다.'
              }
            >
              <Button
                icon={<ApiOutlined />}
                loading={verify.isPending}
                onClick={() => verify.mutate()}
              >
                연결 확인
              </Button>
            </Tooltip>

            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="받을 주소 (발신 주소와 달라야 합니다)"
              style={{ width: 300 }}
              status={selfSend ? 'error' : undefined}
            />
            <Button
              icon={<SendOutlined />}
              loading={sendTest.isPending}
              disabled={!testTo.trim() || selfSend}
              onClick={() => sendTest.mutate()}
            >
              시험 발송
            </Button>
          </Space>

          {selfSend && (
            <Alert
              type="error"
              showIcon
              message="받을 주소가 발신 주소와 같습니다"
              description="네이버 클라우드는 자기 자신에게 보내는 것을 거부합니다. 다른 주소(개인 메일 등)로 받아 보세요."
            />
          )}

          {check && (
            <Alert
              type={check.ok ? 'success' : 'error'}
              showIcon
              message={check.ok ? '연결에 성공했습니다' : '연결하지 못했습니다'}
              description={check.message}
            />
          )}

          {/*
            최근 발송과 **발송처가 말하는 실제 결과**.

            우리 이력의 「발송됨」만으로는 도착 여부를 알 수 없다. 네이버
            클라우드는 접수와 발송이 나뉘어 있어서, 접수된 뒤 조용히 실패한
            건을 여기서 눌러 확인한다.
          */}
          {(recent.data ?? []).length > 0 && (
            <Card size="small" title="최근 발송" styles={{ body: { padding: 0 } }}>
              <Table<RecentMail>
                rowKey="messageLogId"
                size="small"
                pagination={false}
                dataSource={recent.data ?? []}
                expandable={{
                  expandedRowRender: (row) => (
                    <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                      {statusOf[row.messageLogId] ??
                        row.errorMessage ??
                        '「발송처에 확인」을 누르면 실제 결과를 물어봅니다.'}
                    </Text>
                  ),
                }}
                columns={[
                  {
                    title: '시각',
                    dataIndex: 'queuedAt',
                    width: 150,
                    render: (v: string) => new Date(v).toLocaleString('ko-KR'),
                  },
                  { title: '서식', dataIndex: 'templateCode', width: 160 },
                  {
                    title: '우리 기록',
                    dataIndex: 'status',
                    width: 110,
                    render: (v: string) =>
                      v === 'SENT' ? <Tag color="blue">접수됨</Tag> : <Tag color="red">{v}</Tag>,
                  },
                  { title: '경로', dataIndex: 'provider', width: 120 },
                  {
                    title: '',
                    width: 130,
                    render: (_, row) =>
                      row.providerMsgId ? (
                        <Button size="small" onClick={() => void askStatus(row)}>
                          발송처에 확인
                        </Button>
                      ) : null,
                  },
                ]}
              />
            </Card>
          )}

          {data && (
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="설정 출처">
                {data.source === 'db' ? '이 화면에서 저장한 값' : '서버 환경설정(.env)'}
              </Descriptions.Item>
              <Descriptions.Item label="마지막 수정">
                {data.updatedAt ? new Date(data.updatedAt).toLocaleString('ko-KR') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="마지막 확인">
                {data.lastTestedAt ? (
                  <Space>
                    <Text>{new Date(data.lastTestedAt).toLocaleString('ko-KR')}</Text>
                    {data.lastTestOk ? (
                      <Tag color="green">성공</Tag>
                    ) : (
                      <Tag color="red">실패</Tag>
                    )}
                    {data.lastTestError && <Text type="danger">{data.lastTestError}</Text>}
                  </Space>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Space>
      </Card>
    </Space>
  );
}
